import React, { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router';

import { Canvas } from 'cvat-canvas-wrapper';
import { CombinedState } from 'reducers';
import {
    activateObject, changeFrameAsync, fetchAnnotationsAsync, saveAnnotationsAsync,
} from 'actions/annotation-actions';
import { isReferenceJob } from 'utils/reference-job';
import type { SceneOverride } from './scene-api';
import { SceneProvider } from './scene-context';

// Wraps the header and the workspace: the header's Scene button opens the panel in the sidebar.
// Always rendered, so the job arriving does not remount the canvas.
export default function SceneProviderContainer({ children }: { children: React.ReactNode }): JSX.Element {
    const dispatch = useDispatch();
    const job = useSelector((state: CombinedState) => state.annotation.job.instance);
    const saving = useSelector((state: CombinedState) => state.annotation.annotations.saving.uploading);
    // serialises the whole collection: called on demand only
    const hasUnsavedChanges = useCallback(() => !!job && job.annotations.hasUnsavedChanges(), [job]);
    const save = useCallback(async () => {
        await dispatch(saveAnnotationsAsync() as any);
        if (job && job.annotations.hasUnsavedChanges()) throw new Error('Your changes could not be saved');
    }, [dispatch, job]);
    const reloadAnnotations = useCallback(async () => {
        await dispatch(fetchAnnotationsAsync(true) as any);
    }, [dispatch]);
    const store = useStore<CombinedState>();
    const history = useHistory();
    // the store keeps the states of the drawing shown now only
    const clientIdOf = useCallback((id: number): number | null => (
        store.getState().annotation.annotations.states.find((state) => state.serverID === id)?.clientID ?? null
    ), [store]);
    const showShape = useCallback(async ({ id, frame, job: jobId }: SceneOverride) => {
        if (!job || !('taskId' in job)) return;
        if (jobId !== job.id) {
            history.push(`/tasks/${job.taskId}/jobs/${jobId}?frame=${frame}`);
            return;
        }
        const { annotation: { canvas: { instance }, player }, settings } = store.getState();
        // drawings are 2D: the 3D canvas has neither focus nor this setup event
        const canvas = instance instanceof Canvas ? instance : null;
        const focus = (): void => {
            const clientID = clientIdOf(id);
            if (clientID === null) return;
            dispatch(activateObject(clientID, null, null));
            canvas?.focus(clientID, settings.workspace.aamZoomMargin);
        };
        if (player.frame.number === frame) {
            focus();
            return;
        }
        // the canvas fits a new drawing as it sets it up, so the focus waits for that
        canvas?.html().addEventListener('canvas.setup', focus, { once: true });
        await dispatch(changeFrameAsync(frame) as any);
    }, [job, history, dispatch, clientIdOf, store]);
    const highlightShape = useCallback((id: number | null) => {
        dispatch(activateObject(id === null ? null : clientIdOf(id), null, null));
    }, [dispatch, clientIdOf]);
    // reference photos have no task scene
    const taskId = job && !isReferenceJob(job) && 'taskId' in job ? job.taskId : null;
    return (
        <SceneProvider
            taskId={taskId ?? null}
            jobId={job?.id ?? null}
            projectId={job?.projectId ?? null}
            saving={saving}
            hasUnsavedChanges={hasUnsavedChanges}
            save={save}
            reloadAnnotations={reloadAnnotations}
            showShape={showShape}
            highlightShape={highlightShape}
        >
            {children}
        </SceneProvider>
    );
}
